"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { BookOpen, History, LineChart, RotateCcw, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { InputForm } from "@/components/calculator/InputForm";
import { ResultCard } from "@/components/calculator/ResultCard";
import { AreaChartWrapper, AreaChartDualAxis, LineChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/utils/currency";
import {
  calculateInflation,
  calculateHistoricalInflation,
  buildUnifiedChartData,
  solveInflationRate,
} from "@/lib/calculators/inflation";
import {
  CPI_MIN_YEAR,
  CPI_MAX_YEAR,
} from "@/lib/data/cpi-by-year";

const MAX_AMOUNT = 999_999_999_999;

const schema = z.object({
  amount: z.coerce
    .number()
    .min(1, "Enter a positive amount")
    .max(MAX_AMOUNT, "Maximum is $999 trillion"),
  inflationRate: z.coerce
    .number()
    .min(0, "Enter a valid rate")
    .max(50),
  years: z.coerce.number().min(1, "Enter at least 1 year").max(50),
  targetFutureAmount: z.coerce
    .number()
    .min(1, "Enter a positive amount")
    .max(MAX_AMOUNT),
  solveMode: z.enum(["amount", "rate"]),
  compareRates: z.union([z.boolean(), z.enum(["true", "false"])]),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  amount: 10000,
  inflationRate: 3,
  years: 10,
  targetFutureAmount: 13439,
  solveMode: "amount",
  compareRates: false,
};

const PRESETS: { label: string; values: Partial<FormValues> }[] = [
  {
    label: "3% (historical avg)",
    values: { amount: 100000, inflationRate: 3, years: 20 },
  },
  {
    label: "5% (higher)",
    values: { amount: 50000, inflationRate: 5, years: 10 },
  },
  {
    label: "Savings erosion",
    values: { amount: 25000, inflationRate: 3, years: 15 },
  },
];

const CHART_COLOR = "#dc2626";
const COMPARE_RATES = [2, 3, 5] as const;
const COMPARE_COLORS = ["#2563eb", "#dc2626", "#16a34a"] as const;

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const amount = params.get("amount");
  const rate = params.get("rate");
  const years = params.get("years");
  const target = params.get("target");
  const mode = params.get("mode");
  if (!amount || !years) return null;
  return {
    amount: parseFloat(amount),
    inflationRate: rate ? parseFloat(rate) : 3,
    years: parseInt(years, 10),
    targetFutureAmount: target ? parseFloat(target) : 13439,
    solveMode: mode === "rate" ? "rate" : "amount",
    compareRates: params.get("compare") === "1",
  };
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set("amount", String(values.amount));
  params.set("rate", String(values.inflationRate));
  params.set("years", String(values.years));
  params.set("target", String(values.targetFutureAmount));
  params.set("mode", values.solveMode);
  params.set("compare", (values.compareRates === "true" || values.compareRates === true) ? "1" : "0");
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

const HISTORICAL_YEARS = Array.from(
  { length: CPI_MAX_YEAR - CPI_MIN_YEAR + 1 },
  (_, i) => CPI_MAX_YEAR - i
);

export function InflationCalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState(() =>
    calculateInflation({ ...defaultValues })
  );
  const [historicalAmount, setHistoricalAmount] = useState(10000);
  const [historicalYear, setHistoricalYear] = useState(1975);

  const historicalResult = calculateHistoricalInflation({
    amount: historicalAmount,
    fromYear: historicalYear,
    toYear: CPI_MAX_YEAR,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged = { ...defaultValues, ...parsed } as FormValues;
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        const data = parsedResult.data as FormValues;
        setFormValues(data);
        if (data.solveMode === "rate") {
          const solved = solveInflationRate(
            data.amount,
            data.targetFutureAmount,
            data.years
          );
          setResult(
            calculateInflation({
              amount: data.amount,
              inflationRate: solved ?? data.inflationRate,
              years: data.years,
            })
          );
        } else {
          setResult(calculateInflation(data));
        }
        setResetKey((k) => k + 1);
      }
    }
  }, []);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setResult(calculateInflation({ ...defaultValues }));
    setResetKey((k) => k + 1);
  }, []);

  const handlePreset = useCallback((preset: Partial<FormValues>) => {
    const merged = { ...defaultValues, ...formValues, ...preset } as FormValues;
    if (merged.solveMode === "rate" && merged.inflationRate != null) {
      merged.targetFutureAmount = Math.round(
        merged.amount * Math.pow(1 + merged.inflationRate / 100, merged.years)
      );
    }
    const parsed = schema.safeParse(merged);
    if (parsed.success) {
      setFormValues(parsed.data);
      if (parsed.data.solveMode === "rate") {
        const solved = solveInflationRate(
          parsed.data.amount,
          parsed.data.targetFutureAmount,
          parsed.data.years
        );
        setResult(
          calculateInflation({
            amount: parsed.data.amount,
            inflationRate: solved ?? parsed.data.inflationRate,
            years: parsed.data.years,
          })
        );
      } else {
        setResult(calculateInflation(parsed.data));
      }
      setResetKey((k) => k + 1);
    }
  }, [formValues]);

  const handleChange = useCallback((values: FormValues) => {
    setFormValues(values);
    if (values.solveMode === "rate") {
      const solvedRate = solveInflationRate(
        values.amount,
        values.targetFutureAmount,
        values.years
      );
      const effectiveRate = solvedRate ?? values.inflationRate;
      setResult(
        calculateInflation({
          amount: values.amount,
          inflationRate: effectiveRate,
          years: values.years,
        })
      );
    } else {
      setResult(calculateInflation({ ...values }));
    }
  }, []);

  const useUnifiedChart = historicalYear < CPI_MAX_YEAR;

  const solvedRate =
    formValues.solveMode === "rate"
      ? solveInflationRate(
          formValues.amount,
          formValues.targetFutureAmount,
          formValues.years
        )
      : null;

  const effectiveRate = formValues.solveMode === "rate" && solvedRate != null
    ? solvedRate
    : formValues.inflationRate;

  const unifiedPoints = useUnifiedChart
    ? buildUnifiedChartData({
        historicalAmount,
        historicalYear,
        inflationRate: effectiveRate,
        projectionYears: formValues.years,
      })
    : null;

  const baseChartData = useUnifiedChart && unifiedPoints
    ? unifiedPoints.map((d) => ({
        label: String(d.year),
        balance: d.amount,
        inflationYoy: d.inflationYoy,
      }))
    : result.chartData.map((d) => ({
        label: `Year ${d.year}`,
        balance: d.futureEquivalent,
      }));

  const isCompareRates =
    formValues.compareRates === "true" || formValues.compareRates === true;
  const comparisonChartData =
    isCompareRates && !useUnifiedChart
      ? result.chartData.map((d) => {
          const point: { label: string; [key: string]: string | number } = {
            label: `Year ${d.year}`,
          };
          COMPARE_RATES.forEach((rate) => {
            const val = formValues.amount * Math.pow(1 + rate / 100, d.year);
            point[`rate_${rate}`] = Math.round(val * 100) / 100;
          });
          return point;
        })
      : null;

  const baseFields = [
    {
      name: "solveMode" as const,
      label: "Solve for",
      tooltip: "Calculate future amount or the rate needed to reach a target.",
      inputType: "select" as const,
      options: [
        { value: "amount", label: "How much will I need?" },
        { value: "rate", label: "What rate would get me there?" },
      ],
    },
    {
      name: "amount" as const,
      label: "Amount today",
      tooltip: "The dollar amount in today's purchasing power.",
      suffix: " USD",
      min: 1,
      max: MAX_AMOUNT,
      step: 1,
      inputMode: "decimal" as const,
    },
    {
      name: "years" as const,
      label: "Time period",
      tooltip: "Number of years to project.",
      suffix: " years",
      min: 1,
      max: 50,
      sliderMax: 30,
      step: 1,
      inputType: "slider-number" as const,
      inputMode: "decimal" as const,
    },
  ];

  const amountModeFields = [
    ...baseFields,
    {
      name: "inflationRate" as const,
      label: "Inflation rate",
      tooltip: "Annual inflation rate (e.g., 3 for 3%). Historical average is ~3%.",
      suffix: "%",
      min: 0,
      max: 50,
      step: 0.5,
      inputType: "slider-number" as const,
      inputMode: "decimal" as const,
    },
  ];

  const rateModeFields = [
    ...baseFields,
    {
      name: "targetFutureAmount" as const,
      label: "Target amount",
      tooltip: "How much you want to have in the future (same purchasing power).",
      suffix: " USD",
      min: 1,
      max: MAX_AMOUNT,
      step: 1,
      inputMode: "decimal" as const,
    },
  ];

  const inputFields = [
    ...(formValues.solveMode === "rate" ? rateModeFields : amountModeFields),
    ...(!useUnifiedChart
      ? [
          {
            name: "compareRates" as const,
            label: "Compare rates",
            tooltip: "Show 2%, 3%, and 5% inflation on the chart.",
            inputType: "select" as const,
            options: [
              { value: "false", label: "No" },
              { value: "true", label: "Yes (2% vs 3% vs 5%)" },
            ],
          },
        ]
      : []),
  ];

  return (
    <CalculatorTemplate
      chartSectionLabel="Purchasing power over time"
      chartSectionIcon={LineChart}
      explanationSectionLabel="Formula & examples"
      explanationSectionIcon={BookOpen}
      inputsSection={
        <div className="space-y-4">
          <InputForm
            key={resetKey}
            schema={schema}
            defaultValues={formValues}
            fields={inputFields}
            onChange={handleChange}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Presets:</span>
            {PRESETS.map((p) => {
              const isSelected = Object.entries(p.values).every(
                ([k, v]) => formValues[k as keyof FormValues] === v
              );
              return (
                <Button
                  key={p.label}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePreset(p.values)}
                  className="text-xs"
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          <ResultCard
            icon={TrendingDown}
            metrics={[
              ...(formValues.solveMode === "rate" && solvedRate != null
                ? [
                    {
                      label: "Required inflation rate",
                      value: `${solvedRate}%`,
                      highlight: true,
                      tooltip:
                        "The annual rate at which prices would need to rise for your amount to reach the target.",
                    },
                  ]
                : [
                    {
                      label: `In ${formValues.years} years you'd need`,
                      value: formatCurrency(result.futureEquivalent),
                      highlight: true,
                      tooltip:
                        "To buy the same things your amount buys today, you'd need this much in the future. Prices rise with inflation.",
                    },
                  ]),
              {
                label: `${formValues.years} years ago, that would've been`,
                value: formatCurrency(result.pastEquivalent),
                tooltip:
                  "This amount from the past had the same buying power as your amount today. Things cost less back then.",
              },
              {
                label: "Inflation erodes your amount by",
                value: formatCurrency(result.purchasingPowerLost),
                tooltip:
                  "The extra amount you'd need in the future just to stay even. Cash in a mattress loses this much value.",
              },
              {
                label: "Inflation multiplier",
                value: `${result.inflationMultiplier}×`,
                tooltip:
                  "Prices rise by this factor. $1 today buys what $1.34 buys in the future.",
              },
            ]}
          />
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">What this means</p>
            <p className="mt-1.5">
              {formValues.solveMode === "rate" && solvedRate != null ? (
                <>
                  To reach {formatCurrency(formValues.targetFutureAmount)} from {formatCurrency(formValues.amount)} in {formValues.years} years, prices would need to rise at {solvedRate}% annually. That&apos;s the inflation rate that would erode today&apos;s purchasing power to your target.
                </>
              ) : (
                <>
                  At {formValues.inflationRate}% inflation, {formatCurrency(formValues.amount)} today will only buy what {formatCurrency(result.futureEquivalent)} buys in {formValues.years} years. Your money loses value over time—that&apos;s why a dollar doesn&apos;t go as far as it used to. Investing aims to grow faster than inflation so your purchasing power doesn&apos;t shrink.
                </>
              )}
            </p>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <History className="size-4 text-primary" />
              Compare to a past year
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Use actual US inflation data (CPI) to see what an amount from the past would be worth today.
            </p>
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <div className="min-w-[120px]">
                <label htmlFor="historical-amount" className="mb-1 block text-xs font-medium text-muted-foreground">
                  Amount
                </label>
                <Input
                  id="historical-amount"
                  type="number"
                  min={1}
                  max={MAX_AMOUNT}
                  value={historicalAmount}
                  onChange={(e) => setHistoricalAmount(Number(e.target.value) || 0)}
                />
              </div>
              <div className="min-w-[100px]">
                <label htmlFor="historical-year" className="mb-1 block text-xs font-medium text-muted-foreground">
                  From year
                </label>
                <select
                  id="historical-year"
                  value={historicalYear}
                  onChange={(e) => setHistoricalYear(Number(e.target.value))}
                  className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {HISTORICAL_YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {historicalResult && (
              <p className="text-sm font-medium text-foreground">
                {formatCurrency(historicalAmount)} in {historicalYear} = {formatCurrency(historicalResult.equivalentAmount)} today
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  ({(historicalResult.totalInflation * 100).toFixed(0)}% total inflation)
                </span>
              </p>
            )}
          </div>
        </div>
      }
      chartSection={
        mounted ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {useUnifiedChart
                ? `Amount needed each year to maintain the purchasing power of ${formatCurrency(historicalAmount)} from ${historicalYear}. Historical data (CPI) through ${CPI_MAX_YEAR}, then projected at ${effectiveRate}% inflation. The gray line shows year-over-year inflation.`
                : isCompareRates
                  ? "Compare how different inflation rates (2%, 3%, 5%) affect your purchasing power over time."
                  : "How much you'd need each year to maintain today's purchasing power. The line rises because inflation makes things cost more over time."}
            </p>
            {useUnifiedChart ? (
              <AreaChartDualAxis
                data={baseChartData}
                primaryKey="balance"
                primaryColor={CHART_COLOR}
                primaryName="Amount needed"
                secondaryKey="inflationYoy"
                secondaryName="YoY inflation"
              />
            ) : isCompareRates && comparisonChartData ? (
              <LineChartWrapper
                data={comparisonChartData}
                dataKeys={COMPARE_RATES.map((rate, i) => ({
                  key: `rate_${rate}`,
                  color: COMPARE_COLORS[i],
                  name: `${rate}% inflation`,
                }))}
              />
            ) : (
              <AreaChartWrapper
                data={baseChartData}
                dataKeys={[
                  {
                    key: "balance",
                    color: CHART_COLOR,
                    name: "Amount needed (future $)",
                  },
                ]}
              />
            )}
          </div>
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              How inflation works
            </h2>
            <p className="mb-3 text-muted-foreground">
              Inflation means prices go up. When inflation is 3%, something that costs $100 today will cost about $103 next year. Your money buys less over time.
            </p>
            <h3 className="mb-2 text-base font-semibold text-foreground">
              The inflation formula
            </h3>
            <p className="mb-3 font-mono text-sm text-foreground">
              Future $ = Today $ × (1 + r)^t
            </p>
            <p className="mb-3 text-muted-foreground">
              Where <strong className="text-foreground">r</strong> = annual
              inflation rate (e.g., 0.03 for 3%) and{" "}
              <strong className="text-foreground">t</strong> = years. For &quot;past equivalent&quot;:
              Today $ ÷ (1 + r)^t.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example calculation
            </h2>
            <p className="mb-3 text-muted-foreground">
              $10,000 at 3% inflation for 10 years:
            </p>
            <ExplanationTable
              headers={["Variable", "Value"]}
              rows={[
                ["Amount (today)", "$10,000"],
                ["Rate (r)", "0.03"],
                ["Years (t)", "10"],
                ["Future equivalent", "10,000 × (1.03)^10 ≈ $13,439"],
                ["Past equivalent", "10,000 ÷ (1.03)^10 ≈ $7,441"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Historical comparison (CPI data)
            </h2>
            <p className="mb-3 text-muted-foreground">
              The &quot;Compare to a past year&quot; section uses actual US CPI-U data from the Bureau of Labor Statistics. The formula is: Today $ = Past $ × (CPI_today ÷ CPI_past). This reflects real inflation over that period, not a hypothetical rate.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Assumes a constant inflation rate; real inflation fluctuates.</li>
              <li>Different goods and services inflate at different rates.</li>
              <li>Use for planning and awareness, not precise predictions.</li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
